import React, { useState, useEffect } from 'react';
import { Typography, Tabs, Form, Input, Button, Upload, Avatar, Row, Col, Card, Select, DatePicker, Divider, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, UploadOutlined, CreditCardOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import api from '../api';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../utils/BaseUrl';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const ProfileContainer = styled.div`
  max-width: 1000px;
  margin: 40px auto;
  padding: 0 24px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const ProfileCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const UserInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0; 
  @media (max-width: 768px) {
    padding: 16px 0;
  }
`;

const AvatarUploader = styled.div`
  margin-bottom: 16px;
  text-align: center;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }
`;

const InformationCustomerPage = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('1');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);


  const storedUser = JSON.parse(localStorage.getItem("user"));
  const userId = storedUser?.userId;

  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      userId = parsed?.UserId || parsed?.userId || null;
    } catch (error) {
      console.error('Invalid user JSON:', error);
    }
  }


  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) {
        console.error('Không tìm thấy userId trong localStorage');
        return;
      }

      try {
        const response = await api.get(`${API_BASE_URL}/api/Auth/profile?id=${userId}`);
        console.log('Profile data:', response.data);
        setUserData(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    if (userData) {
      form.setFieldsValue({
        name: userData.fullName,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        dob: userData.dateOfBirth ? dayjs(userData.dateOfBirth) : null,
        gender: userData.gender,
        country: userData.country,
      });
    }
  }, [userData]);


  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        FullName: values.name,
        Phone: values.phone,
        Address: values.address,
        DateOfBirth: values.dob ? values.dob.toISOString() : null,
        Gender: values.gender,
        Country: values.country,
        Avatar: userData.Avatar || "", // Assuming Avatar is already set in userData
      };
      const response = await api.put(`${API_BASE_URL}/api/Auth/profile/${userId}`, payload);
      setUserData(response.data);
      localStorage.setItem('user', JSON.stringify({
        UserId: response.data.userId,
        Email: response.data.email,
        FullName: response.data.fullName,
      }));
      message.success('Profile updated successfully');
    } catch (error) {
      message.error('Failed to update profile');
      console.error('Error updating profile:', error);
    }
    setLoading(false);
  };

  const onPasswordChange = async (values) => {
    setLoading(true);
    try {
      const payload = {
        CurrentPassword: values.currentPassword,
        NewPassword: values.newPassword,
      };
      await api.post('/api/Auth/change-password', payload);
      message.success('Password changed successfully');
      passwordForm.resetFields();
    } catch (error) {
      message.error('Failed to change password');
      console.error('Error changing password:', error);
    }
    setLoading(false);
  };

  if (loading && !userData) {
    return <div>Loading...</div>;
  }

  if (!userData && !loading) {
    return <div>Error loading profile. Please try again.</div>;
  }

  return (
    <ProfileContainer>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
        My Profile
      </Title>

      <ProfileCard>
        <UserInfoSection>
          <AvatarUploader>
            <Avatar
              size={100}
              icon={<UserOutlined />}
              src={userData.Avatar || 'https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png'}
            />
            <div style={{ marginTop: 16 }}>
              <Upload showUploadList={false} disabled>
                <Button icon={<UploadOutlined />}>Change Photo</Button>
              </Upload>
            </div>
          </AvatarUploader>
          <Title level={4}>{userData.FullName}</Title>
          <Text type="secondary">{userData.Email}</Text>
        </UserInfoSection>

        <Divider style={{ margin: '0 0 24px' }} />

        <StyledTabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Personal Information" key="1">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="name"
                    label="Full Name"
                    rules={[{ required: true, message: 'Please enter your name' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Full Name" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email" disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="phone"
                    label="Phone Number"
                    rules={[
                      { required: true, message: 'Please enter your phone number' },
                      { pattern: /^\+?\d{10,12}$/, message: 'Invalid phone number' }
                    ]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="dob"
                    label="Date of Birth"
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="gender"
                    label="Gender"
                  >
                    <Select placeholder="Select gender">
                      <Option value="male">Male</Option>
                      <Option value="female">Female</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="country"
                    label="Country"
                  >
                    <Input placeholder="Country" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="address"
                label="Address"
              >
                <Input placeholder="Address" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Change Password" key="2">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={onPasswordChange}
            >
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: 'Please enter your current password' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Current Password" />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your new password' },
                  { min: 8, message: 'Password must be at least 8 characters' }
                ]}
                hasFeedback
              >
                <Input.Password prefix={<LockOutlined />} placeholder="New Password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                hasFeedback
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Confirm New Password" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Membership" key="4">
            <ProfileCard>
              <Title level={4}>Current Membership</Title>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Plan: </Text>
                <Text>Premium Quarterly</Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Start Date: </Text>
                <Text>January 15, 2023</Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Expiry Date: </Text>
                <Text>April 15, 2023</Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Status: </Text>
                <Text style={{ color: '#52c41a' }}>Active</Text>
              </div>

              <Button type="primary" style={{ marginTop: 16 }}>
                Upgrade Membership
              </Button>
            </ProfileCard>

            <ProfileCard title="Membership History">
              <div style={{ marginBottom: 16 }}>
                <Text strong>Oct 15, 2022 - Jan 15, 2023: </Text>
                <Text>Premium Quarterly</Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Jul 15, 2022 - Oct 15, 2022: </Text>
                <Text>Premium Quarterly</Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Jun 15, 2022 - Jul 15, 2022: </Text>
                <Text>Basic Monthly</Text>
              </div>
            </ProfileCard>
          </TabPane>
        </StyledTabs>
      </ProfileCard>
    </ProfileContainer>
  );
};

export default InformationCustomerPage; 